import type { Vec2 } from '../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import { buildArchFaces } from './archBridgeFaces';

/** The height of a bridge's walking surface at a distance along its axis. */
export type AlongHeight = (x: number) => number;

/**
 * A masonry arch bridge in its frame: x runs along the axis from 0 to `length`, y is world height
 * and z runs across, to the right of the axis. Every value comes from outside: the arch count and
 * spans, pier width, springing and crown heights and parapet from the painting readings, and the
 * deck profile from site-geo's deck function.
 */
export interface ArchBridgeSpec {
  readonly length: number;
  /** Between the parapets' outer faces. */
  readonly width: number;
  /** The clear span of each arch, in order along the axis. */
  readonly spans: readonly number[];
  readonly pierWidth: number;
  /** Where the first arch springs; without it the arcade is centred between equal abutments. */
  readonly firstArchAt?: number;
  readonly springY: number;
  /** The intrados at each arch's crown. */
  readonly crownY: number;
  /** A crown for each arch in turn, where the deck over them is not level. */
  readonly crowns?: readonly number[];
  /** The foot of the masonry, below the water. */
  readonly footY: number;
  readonly parapet: { readonly height: number; readonly thickness: number };
  readonly deckY: AlongHeight;
  /** A compound building may supply recessed masonry end walls separately. */
  readonly endFaces?: boolean;
  /** A roof can close the top of an inhabited gate vault. */
  readonly topFaces?: boolean;
}

export interface Arch {
  readonly left: number;
  readonly right: number;
  /** Its own crown, where the spec names one for each arch. */
  readonly crownY?: number;
}

/** A place along the axis where the faces break: the underside just before and just after it. */
export interface Station {
  readonly x: number;
  readonly before: number;
  readonly after: number;
}

// An arch ring is at least one brick deep, and 15th-century Amsterdam bricks are 19-19.5 cm long
// (Jayasena 2019, Graaf- en modderwerk, p. 151 note 603).
export const MIN_CROWN_COVER_M = 0.2;
// A walkway edge may part from the deck profile by this much at its middle before it is halved.
export const ARCH_DECK_TOLERANCE_M = 0.002;
const MAX_ARC_STEP_RAD = Math.PI / 16;
const MAX_DECK_STEP_M = 2;
const MAX_HALVINGS = 12;
// A deck station this close to an underside station would leave a sliver column; the underside one stays.
const MERGE_M = 1e-3;
const EPSILON = 1e-9;

export function archesOf(spec: ArchBridgeSpec): Arch[] {
  const arcade = spec.spans.reduce((sum, span) => sum + span, 0);
  const total = arcade + (spec.spans.length - 1) * spec.pierWidth;
  const start = spec.firstArchAt ?? (spec.length - total) / 2;
  return spec.spans.reduce<Arch[]>((arches, span, i) => {
    const last = arches[arches.length - 1];
    const left = last ? last.right + spec.pierWidth : start;
    const crownY = spec.crowns?.[i];
    const arch = { left, right: left + span };
    return [...arches, crownY === undefined ? arch : { ...arch, crownY }];
  }, []);
}

function crownOf(spec: ArchBridgeSpec, arch: Arch): number {
  return arch.crownY ?? spec.crownY;
}

function isStilted(spec: ArchBridgeSpec, arch: Arch): boolean {
  return crownOf(spec, arch) - spec.springY >= (arch.right - arch.left) / 2;
}

// A segmental arch while the rise is at most half the span; beyond that a round arch on upright jambs.
export function intradosY(spec: ArchBridgeSpec, arch: Arch, x: number): number {
  const half = (arch.right - arch.left) / 2;
  const off = Math.min(Math.abs(x - (arch.left + half)), half);
  const crown = crownOf(spec, arch);
  const rise = crown - spec.springY;
  if (isStilted(spec, arch)) return crown - half + Math.sqrt(half * half - off * off);
  const radius = (half * half + rise * rise) / (2 * rise);
  return crown - radius + Math.sqrt(radius * radius - off * off);
}

// Where the curve leaves the jambs: the springing, or the top of a stilted arch's jambs.
function curveFootY(spec: ArchBridgeSpec, arch: Arch): number {
  return isStilted(spec, arch) ? crownOf(spec, arch) - (arch.right - arch.left) / 2 : spec.springY;
}

// The x of every point on the curve, from the left springing over the crown to the right one. An
// even count of equal steps puts a point exactly on the crown, where the cover is checked.
function curveXs(spec: ArchBridgeSpec, arch: Arch): number[] {
  const half = (arch.right - arch.left) / 2;
  const rise = crownOf(spec, arch) - spec.springY;
  const radius = isStilted(spec, arch) ? half : (half * half + rise * rise) / (2 * rise);
  const from = Math.acos(-half / radius);
  const steps = 2 * Math.ceil((2 * from - Math.PI) / (2 * MAX_ARC_STEP_RAD));
  const centre = arch.left + half;
  return Array.from({ length: steps + 1 }, (_, i) => {
    if (i === 0) return arch.left;
    if (i === steps) return arch.right;
    if (2 * i === steps) return centre;
    return centre + radius * Math.cos(from - (i * (2 * from - Math.PI)) / steps);
  });
}

// Halves each step until its middle keeps within the tolerance of the profile.
function halved(deckY: AlongHeight, a: number, b: number, depth: number): number[] {
  const middle = (a + b) / 2;
  const bend = Math.abs(deckY(middle) - (deckY(a) + deckY(b)) / 2);
  if (bend <= ARCH_DECK_TOLERANCE_M || depth >= MAX_HALVINGS) return [a];
  return [...halved(deckY, a, middle, depth + 1), ...halved(deckY, middle, b, depth + 1)];
}

// Points inside one straight stretch of the profile add nothing to it.
function withoutStraightRuns(xs: readonly number[], deckY: AlongHeight): number[] {
  return xs.filter((x, i) => {
    if (i === 0 || i === xs.length - 1) return true;
    const [a, b] = [xs[i - 1]!, xs[i + 1]!];
    const chord = deckY(a) + ((deckY(b) - deckY(a)) * (x - a)) / (b - a);
    return Math.abs(deckY(x) - chord) > EPSILON;
  });
}

/** Where the deck profile needs a break for the walkway to keep within the tolerance. */
export function deckStations(spec: ArchBridgeSpec): number[] {
  const steps = Math.max(1, Math.ceil(spec.length / MAX_DECK_STEP_M));
  const coarse = Array.from({ length: steps }, (_, i) => (i * spec.length) / steps);
  const fine = coarse.flatMap((a, i) => halved(spec.deckY, a, coarse[i + 1] ?? spec.length, 0));
  return withoutStraightRuns([...fine, spec.length], spec.deckY);
}

function undersideAt(spec: ArchBridgeSpec, arches: readonly Arch[], x: number): Station {
  const flat: Station = { x, before: spec.footY, after: spec.footY };
  const arch = arches.find((a) => x >= a.left && x <= a.right);
  if (!arch) return flat;
  if (x === arch.left) return { ...flat, after: curveFootY(spec, arch) };
  if (x === arch.right) return { ...flat, before: curveFootY(spec, arch) };
  const y = intradosY(spec, arch, x);
  return { x, before: y, after: y };
}

/** Every break along the axis: the ends, the arch curves, and the deck profile's own stations. */
export function stationsOf(spec: ArchBridgeSpec): Station[] {
  const arches = archesOf(spec);
  const under = [0, ...arches.flatMap((arch) => curveXs(spec, arch)), spec.length];
  const deck = deckStations(spec).filter((x) => under.every((u) => Math.abs(u - x) > MERGE_M));
  return [...under, ...deck].sort((a, b) => a - b).map((x) => undersideAt(spec, arches, x));
}

/** The underside from the start end to the far end: foot, abutments, jambs and intrados. */
export function undersideChain(stations: readonly Station[]): Vec2[] {
  return stations.flatMap((s): Vec2[] =>
    s.before === s.after
      ? [[s.x, s.before]]
      : [
          [s.x, s.before],
          [s.x, s.after],
        ]
  );
}

/** The outline of a side face, counter-clockwise: the underside, then the parapet top back. */
export function sideProfile(spec: ArchBridgeSpec): Vec2[] {
  checkSpec(spec);
  const stations = stationsOf(spec);
  const top = stations.map((s): Vec2 => [s.x, spec.deckY(s.x) + spec.parapet.height]).reverse();
  return [...undersideChain(stations), ...top];
}

function fail(message: string): never {
  throw new RangeError(`Arch bridge: ${message}`);
}

function checkSizes(spec: ArchBridgeSpec): void {
  if (!(spec.length > 0 && spec.width > 0 && spec.pierWidth > 0)) {
    fail('length, width and pier width must be positive');
  }
  if (spec.spans.length === 0 || spec.spans.some((span) => !(span > 0))) {
    fail('it needs at least one arch, and every span must be positive');
  }
  const { height, thickness } = spec.parapet;
  if (!(
    (height === 0 && thickness === 0) ||
    (height > 0 && thickness > 0 && 2 * thickness < spec.width)
  )) {
    fail('the parapets must have a size and leave a walkway between them');
  }
  if (spec.crowns && spec.crowns.length !== spec.spans.length) {
    fail('where crowns are named, every arch needs one');
  }
  const crowns = spec.crowns ?? [spec.crownY];
  if (!(spec.footY < spec.springY && crowns.every((crown) => spec.springY < crown))) {
    fail('the foot must lie below the springings, and the springings below the crown');
  }
}

function checkSpec(spec: ArchBridgeSpec): void {
  checkSizes(spec);
  const arches = archesOf(spec);
  if (!(arches[0]!.left > 0 && arches[arches.length - 1]!.right < spec.length)) {
    fail('the arches must fit between the two ends, with an abutment at each');
  }
  for (const arch of arches) {
    for (const x of curveXs(spec, arch)) {
      if (spec.deckY(x) - intradosY(spec, arch, x) < MIN_CROWN_COVER_M - EPSILON) {
        fail(`the intrados at ${x.toFixed(2)} m comes within ${MIN_CROWN_COVER_M} m of the deck`);
      }
    }
  }
}

/**
 * The bridge as one closed solid facing outward: side faces with the arches cut from them, the
 * underside through every arch, the walkway on the deck profile between two parapets, and the two
 * end faces. Throws a RangeError when the arches do not fit or come within the cover of the deck.
 */
export function buildArchBridge(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  spec: ArchBridgeSpec
): void {
  checkSpec(spec);
  const stations = stationsOf(spec);
  buildArchFaces({ builder, style, frame, spec }, stations, undersideChain(stations));
}
